import React, {useState, useEffect, useRef, useCallback} from 'react'
import {AdvancedMarker, APIProvider, InfoWindow, Map, Pin} from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import ServiceCenterTooltip from './service-center-tooltip'
import {Box} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {remCalc} from '../../theme/_utils'

const ServiceCenterMaps = ({...props}) => {
    const [infoWindow, setInfoWindow] = useState({})
    const [infoWindowShown, setInfoWindowShown] = useState(false)
    const {customParams} = getConfig()
    const INITIAL_CAMERA = {
        center: {
            lat: props?.selectedStore?.latitude || props?.fallbackCoordinates?.latitude || 40,
            lng: props?.selectedStore?.longitude || props?.fallbackCoordinates?.longitude || 0,
        },
        zoom: 5,
    }
    const [cameraProps, setCameraProps] = useState(INITIAL_CAMERA)

    const useStyleInfoWindow = () => {
        useEffect(() => {
            const style = document.createElement('style')
            style.innerHTML = `
            .gm-style {
              font: inherit !important
            }
            .gm-style-iw {
              max-width: ${remCalc(270)} !important;
              border-radius: 0 !important;
            }
            .gm-style-iw-ch {
                padding-top: 0;
            }
            .gm-style .gm-style-iw-c {
                padding: ${remCalc(16)} !important;
            }
            .gm-style-iw-chr {
                align-items: center;
                justify-content: space-between;
            }
            .gm-ui-hover-effect {
                width: ${remCalc(16)} !important;
                height: ${remCalc(16)} !important;
            }
            .gm-ui-hover-effect > span {
                width: ${remCalc(16)} !important;
                height: ${remCalc(16)} !important;
                margin: 0 !important;
            }
            @media (min-width: 768px) {
                .gm-style-iw {
                    max-width: ${remCalc(342)} !important;
                }
            }
          `
            document.head.appendChild(style)

            return () => {
                document.head.removeChild(style)
            }
        }, [])
    }
    useStyleInfoWindow()

    useEffect(() => {
        setInfoWindow(props.selectedStore)
    }, [])

    useEffect(() => {
        if (props.selectedStore && Object.keys(props.selectedStore).length > 0) {
            setInfoWindow(props.selectedStore)
            setInfoWindowShown(true)
        }
    }, [props.selectedStore])

    useEffect(() => {
        if (props.searchedLocation && Object.keys(props.searchedLocation).length > 0) {
            setCameraProps({
                center: {
                    lat: props.searchedLocation?.lat,
                    lng: props.searchedLocation?.lng,
                },
                zoom: props.searchedLocation?.zoom || 10,
            })
        }
    }, [props.searchedLocation])

    const handleCameraChange = useCallback((ev) => {
        setCameraProps(ev.detail)
    }, [])

    const markerRefs = useRef({})

    const handleMarkerClick = useCallback(
        (store) => {
            // Focus on the marker when clicked
            props.setSelectedStore(store)
            markerRefs.current[store.id]?.focus()
            setTimeout(() => {
                setInfoWindowShown(true)
            }, 1000)
        },
        [props.setSelectedStore],
    )

    const handleInfoWindowClose = useCallback(() => {
        setInfoWindowShown(false)
        props.setSelectedStore({})
        setInfoWindow({})
        markerRefs.current[props.selectedStore?.id]?.blur()
    }, [props.setSelectedStore, props.selectedStore?.id])

    return (
        <APIProvider apiKey={customParams.googleMapsApiKey} libraries={['marker', 'places']}>
            <Map
                {...cameraProps}
                onCameraChanged={handleCameraChange}
                onDragstart={handleInfoWindowClose}
                gestureHandling={'greedy'}
                disableDefaultUI
                height={remCalc(572)}
                options={{
                    mapId: customParams.googleMapsId,
                    mapTypeControl: true,
                    fullscreenControl: true,
                    zoomControl: true,
                    cameraControl: true,
                    streetViewControl: true,
                }}
            >
                {props.stores.map((store, index) => (
                    <Box key={store.id}>
                        <AdvancedMarker
                            ref={(ref) => (markerRefs.current[store.id] = ref)}
                            position={{
                                lat: store?.latitude,
                                lng: store?.longitude,
                            }}
                            title={'AdvancedMarker with customized pin.'}
                            onClick={() => handleMarkerClick(store)}
                        >
                            <Pin
                                background={'black'}
                                borderColor={'#1e89a1'}
                                glyph={String(index + 1) || ''}
                                glyphColor={'white'}
                            ></Pin>
                        </AdvancedMarker>
                    </Box>
                ))}

                {infoWindowShown && (
                    <InfoWindow
                        position={{
                            lat: props?.selectedStore?.latitude || 40,
                            lng: props?.selectedStore?.longitude || 0,
                        }}
                        anchor={markerRefs.current[props.selectedStore?.id]}
                        disableAutoPan={false}
                        shouldFocus={true}
                        onCloseClick={() => {
                            handleInfoWindowClose()
                        }}
                        headerContent={
                            <Box textStyle={'head-xsmall'} fontWeight="500">
                                {infoWindow?.name}
                            </Box>
                        }
                        className="dl-info-window"
                    >
                        {props.selectedStore && <ServiceCenterTooltip selectedStore={infoWindow} />}
                    </InfoWindow>
                )}
            </Map>
        </APIProvider>
    )
}

ServiceCenterMaps.propTypes = {
    selectedStore: PropTypes.object,
    fallbackCoordinates: PropTypes.object,
    stores: PropTypes.array,
    setSelectedStore: PropTypes.func,
    searchedLocation: PropTypes.object,
}

export default ServiceCenterMaps
